<template>
  <!-- Floating Button -->
  <button
    v-if="!isOpen && tocItems.length > 0"
    @click="toggleTOC"
    class="fixed bottom-6 right-6 z-50 w-12 h-12 bg-accent-600 text-white rounded-full shadow-lg hover:bg-accent-700 hover:scale-110 transition-all duration-200"
    aria-label="Abrir índice de contenidos"
    aria-expanded="false"
  >
    <ListBulletIcon class="w-6 h-6 mx-auto" />
  </button>

  <!-- Overlay -->
  <Transition name="fade">
    <div
      v-if="isOpen"
      @click="toggleTOC"
      class="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
      aria-hidden="true"
    >
      <!-- Sidebar -->
      <Transition name="slide">
        <div
          v-if="isOpen"
          @click.stop
          class="absolute right-0 top-0 h-full w-80 bg-white shadow-2xl overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="toc-title"
        >
          <!-- Header -->
          <div class="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
            <h3 id="toc-title" class="text-lg font-bold text-legal-900">
              Índice de contenidos
            </h3>
            <button
              @click="toggleTOC"
              class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Cerrar índice"
            >
              <XMarkIcon class="w-5 h-5 text-gray-600" />
            </button>
          </div>

          <!-- TOC Items -->
          <nav class="p-6 space-y-1" aria-label="Tabla de contenidos">
            <a
              v-for="item in tocItems"
              :key="item.id"
              :href="`#${item.id}`"
              @click="handleTOCClick($event, item.id)"
              :class="[
                'block py-2 px-3 rounded-lg text-sm transition-colors duration-200',
                'hover:bg-accent-50 hover:text-accent-700',
                activeHeading === item.id ? 'bg-accent-100 text-accent-700 font-medium' : 'text-gray-700',
                getHeadingIndent(item.level)
              ]"
              :aria-current="activeHeading === item.id ? 'true' : 'false'"
            >
              {{ item.text }}
            </a>
          </nav>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { ListBulletIcon, XMarkIcon } from '@heroicons/vue/24/outline'

/**
 * FloatingTOC Component
 *
 * Progressive disclosure table of contents with floating button.
 * Implements UX best practices:
 * - Collapsed by default (reduces distraction)
 * - Floating button bottom-right (easy thumb access on mobile)
 * - Overlay sidebar for full TOC navigation
 * - Keyboard accessible (ESC to close)
 * - Focus trap when open
 * - Smooth scroll to headings
 *
 * @component
 */

// Props
const props = defineProps({
  /**
   * Array of table of contents items
   * @type {Array<{id: string, text: string, level: number}>}
   * @required
   */
  tocItems: {
    type: Array,
    required: true
  },

  /**
   * Currently active heading ID
   * @type {string}
   */
  activeHeading: {
    type: String,
    default: ''
  }
})

// Emits
const emit = defineEmits(['heading-click'])

// Reactive state
const isOpen = ref(false)

// Methods
/**
 * Toggles TOC sidebar visibility
 * Manages body scroll lock
 */
const toggleTOC = () => {
  isOpen.value = !isOpen.value
  document.body.style.overflow = isOpen.value ? 'hidden' : ''

  // Update aria-expanded on button
  const button = document.querySelector('[aria-label="Abrir índice de contenidos"]')
  if (button) {
    button.setAttribute('aria-expanded', isOpen.value.toString())
  }

  // Focus first item when opening
  if (isOpen.value) {
    setTimeout(() => {
      const firstLink = document.querySelector('nav[aria-label="Tabla de contenidos"] a')
      if (firstLink) firstLink.focus()
    }, 100)
  }
}

/**
 * Handles TOC item click
 * Emits event and closes sidebar
 * @param {Event} event - Click event
 * @param {string} id - Heading ID to scroll to
 */
const handleTOCClick = (event, id) => {
  event.preventDefault()
  emit('heading-click', id)
  toggleTOC() // Close after click
}

/**
 * Returns appropriate margin class based on heading level
 * @param {number} level - Heading level (1-6)
 * @returns {string} Tailwind margin class
 */
const getHeadingIndent = (level) => {
  const indents = {
    1: 'ml-0',
    2: 'ml-2',
    3: 'ml-4',
    4: 'ml-6',
    5: 'ml-8',
    6: 'ml-10'
  }
  return indents[level] || 'ml-4'
}

/**
 * Handles ESC key to close TOC
 * @param {KeyboardEvent} e - Keyboard event
 */
const handleEscape = (e) => {
  if (e.key === 'Escape' && isOpen.value) {
    toggleTOC()
  }
}

/**
 * Focus trap implementation
 * Keeps focus within TOC when open
 * @param {KeyboardEvent} e - Keyboard event
 */
const handleFocusTrap = (e) => {
  if (!isOpen.value) return
  if (e.key !== 'Tab') return

  const focusableElements = document.querySelectorAll(
    'nav[aria-label="Tabla de contenidos"] a, button[aria-label="Cerrar índice"]'
  )

  if (focusableElements.length === 0) return

  const firstElement = focusableElements[0]
  const lastElement = focusableElements[focusableElements.length - 1]

  if (e.shiftKey && document.activeElement === firstElement) {
    e.preventDefault()
    lastElement.focus()
  } else if (!e.shiftKey && document.activeElement === lastElement) {
    e.preventDefault()
    firstElement.focus()
  }
}

// Lifecycle
onMounted(() => {
  document.addEventListener('keydown', handleEscape)
  document.addEventListener('keydown', handleFocusTrap)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
  document.removeEventListener('keydown', handleFocusTrap)
  document.body.style.overflow = '' // Ensure scroll is restored
})
</script>

<style scoped>
@reference "../../assets/styles/main.css";

/* Transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.slide-enter-active, .slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from, .slide-leave-to {
  transform: translateX(100%);
}

/* Floating button styles */
.fixed.bottom-6.right-6 {
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1),
              0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.fixed.bottom-6.right-6:hover {
  box-shadow: 0 20px 25px -5px rgba(20, 184, 166, 0.3),
              0 10px 10px -5px rgba(20, 184, 166, 0.2);
}

/* Focus states for accessibility */
button:focus,
a:focus {
  outline: 2px solid theme('colors.accent.600');
  outline-offset: 2px;
}

/* Active item highlight */
a[aria-current="true"] {
  position: relative;
}

a[aria-current="true"]::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: theme('colors.accent.600');
  border-radius: 0 2px 2px 0;
}

/* Sidebar max height for mobile */
@media (max-width: 640px) {
  .w-80 {
    width: 100%;
    max-width: 320px;
  }
}

/* Scroll behavior */
nav[aria-label="Tabla de contenidos"] {
  scroll-behavior: smooth;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .bg-accent-600 {
    background-color: #0d7377;
  }

  .text-gray-700 {
    color: #1a1a1a;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .fade-enter-active,
  .fade-leave-active,
  .slide-enter-active,
  .slide-leave-active {
    transition-duration: 0.01ms !important;
  }

  .hover\:scale-110:hover {
    transform: none;
  }
}

/* Ensure sidebar is readable on small heights */
@media (max-height: 600px) {
  .sticky.top-0 {
    position: relative;
  }
}
</style>
